import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation, FilterType } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useMaterialCategoryApi from "hooks/api/private/useMaterialCategoryApi";
import useSnackbar from "hooks/useSnackbar";

import { MaterialCategoryListItem } from "types/api/materialCategory";

export type MaterialCategoryListHook = {
    navigateToNewMaterialCategory: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    staticFilterHook: StaticTableFilterHook;
    dataTableHook: DataTableHook<MaterialCategoryListItem>;
    alertConfirmHook: AlertConfirmHookType;
};

function useMaterialCategoryList(): MaterialCategoryListHook {
    const navigate = useNavigate();

    const { getAllMaterialCategory, deleteMaterialCategory } = useMaterialCategoryApi();

    const onEditClick = React.useCallback(
        (row: MaterialCategoryListItem) => {
            navigate(makeUrl(":materialCategoryId/modifica", { materialCategoryId: row.id }));
        },
        [navigate]
    );

    const navigateToNewMaterialCategory = React.useCallback(() => {
        navigate("nuova");
    }, [navigate]);

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    name: {
                        key: "material_category_languages.name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        []
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "material-category-list-search",
        memoizedFieldsForSearch
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.search.name = "" + event.target.value;

                return newForm;
            }),
        [setFiltersForm]
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            type: {
                key: "material_category.type",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        []
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "material-category-list-static",
        memoizedFieldsForStaticFilters
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters]
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Categoria Materiale",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteMaterialCategoryButton = React.useCallback(
        (row: MaterialCategoryListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare la categoria?", () => {
                const [apiCall] = deleteMaterialCategory({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Categoria eliminata con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione della categoria"));
            });
        },
        [
            setTriggerReloadTable,
            enqueueSuccessSnackbar,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteMaterialCategory,
        ]
    );

    const columns: Array<Column<MaterialCategoryListItem>> = React.useMemo(
        () => [
            {
                id: "graphical_order",
                header: "Ordine",
                width: "85px",
                render: (row: MaterialCategoryListItem) => row.graphical_order,
                orderable: true,
            },
            {
                id: "name",
                header: "Nome",
                render: (row: MaterialCategoryListItem) => row.name,
                orderable: true,
            },
            {
                id: "type",
                header: "Tipologia",
                width: "100px",
                render: (row: MaterialCategoryListItem) => row.type,
                orderable: true,
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: MaterialCategoryListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={onEditClick}
                        onDelete={deleteMaterialCategoryButton}
                    />
                ),
            },
        ],
        [onEditClick, deleteMaterialCategoryButton]
    );

    const dataTableHook = useDataTableWithMemory<MaterialCategoryListItem>({
        apiCall: getAllMaterialCategory,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "material-category-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();
            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    return React.useMemo(
        (): MaterialCategoryListHook => ({
            navigateToNewMaterialCategory,
            onSearchFilterChange,
            filterSearchHook,
            staticFilterHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [
            navigateToNewMaterialCategory,
            onSearchFilterChange,
            filterSearchHook,
            staticFilterHook,
            alertConfirmHook,
            dataTableHook,
        ]
    );
}

export default useMaterialCategoryList;
